import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { getErrorData, type IErrorData } from '@/utils/getErrorData';
import {
  acceptPaymentRefundRequest,
  createPaymentRefundRequest,
  fetchPaymentRefundRequests,
  rejectPaymentRefundRequest
} from '@/hooks/paymentRefundApi';
import { type IPaymentRefundList } from '@/types/IPaymentRefundList';
import { type IPaymentRefundRequest } from '@/types/IPaymentRefundRequest';
import { type IPaymentRefundsQuery } from '@/types/IPaymentRefundsQuery';
import {
  parsePaymentRefundList,
  parsePaymentRefundResponse
} from '@/utils/paymentRefundParsers';
import { refreshOrderDataV2Thunk } from '@/store/order/orderThunks';
import { fetchOrderPaymentsThunk } from '@/store/payments/paymentsThunks';

/** Аргументы создания заявки: тело запроса плюс заказ для обновления карточки. */
interface ICreateRefundArgs {
  paymentId: number;
  amount: number;
  fullRefund: boolean;
  comment: string;
  orderUuid: string;
}

/** Аргументы решения по заявке: заказ обновляется, только если известен. */
interface IResolveRefundArgs {
  refundUuid: string;
  orderUuid?: string;
}

export const fetchPaymentRefundsThunk = createAsyncThunk<
  IPaymentRefundList,
  IPaymentRefundsQuery,
  { rejectValue: IErrorData }
>('paymentRefunds/fetchList', async (query, { rejectWithValue }) => {
  try {
    const payload = await fetchPaymentRefundRequests(query);
    return parsePaymentRefundList(payload);
  } catch (error) {
    const err = getErrorData(error);
    return rejectWithValue(err);
  }
});

export const createPaymentRefundThunk = createAsyncThunk<
  IPaymentRefundRequest | null,
  ICreateRefundArgs,
  { rejectValue: IErrorData }
>(
  'paymentRefunds/create',
  async (
    { paymentId, amount, fullRefund, comment, orderUuid },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const payload = await createPaymentRefundRequest({
        payment: paymentId,
        amount,
        full_refund: fullRefund,
        comment
      });
      await dispatch(refreshOrderDataV2Thunk({ uuid: orderUuid }));
      await dispatch(fetchOrderPaymentsThunk({ orderUuid }));
      toast.success('Заявка на возврат создана');
      return parsePaymentRefundResponse(payload);
    } catch (error) {
      const err = getErrorData(error);
      return rejectWithValue(err);
    }
  }
);

export const acceptPaymentRefundThunk = createAsyncThunk<
  IPaymentRefundRequest | null,
  IResolveRefundArgs,
  { rejectValue: IErrorData }
>(
  'paymentRefunds/accept',
  async ({ refundUuid, orderUuid }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await acceptPaymentRefundRequest(refundUuid);
      if (orderUuid !== undefined) {
        await dispatch(refreshOrderDataV2Thunk({ uuid: orderUuid }));
        await dispatch(fetchOrderPaymentsThunk({ orderUuid }));
      }
      toast.success('Заявка на возврат принята');
      return parsePaymentRefundResponse(payload);
    } catch (error) {
      const err = getErrorData(error);
      return rejectWithValue(err);
    }
  }
);

export const rejectPaymentRefundThunk = createAsyncThunk<
  IPaymentRefundRequest | null,
  IResolveRefundArgs,
  { rejectValue: IErrorData }
>(
  'paymentRefunds/reject',
  async ({ refundUuid, orderUuid }, { rejectWithValue, dispatch }) => {
    try {
      const payload = await rejectPaymentRefundRequest(refundUuid);
      if (orderUuid !== undefined) {
        await dispatch(refreshOrderDataV2Thunk({ uuid: orderUuid }));
        await dispatch(fetchOrderPaymentsThunk({ orderUuid }));
      }
      toast.success('Заявка на возврат отклонена');
      return parsePaymentRefundResponse(payload);
    } catch (error) {
      const err = getErrorData(error);
      return rejectWithValue(err);
    }
  }
);
